import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import {
  AstSignMailHandoffStatus,
  type AstSignMailState,
} from 'features/ast-signmail/interfaces';
import type { TransferAstRelayResponse } from 'features/call/call-base/interfaces';

const RESET_CALL_ACTION_TYPE = 'call/resetCall';

export const initialAstSignMailState: AstSignMailState = {
  handoffStatus: AstSignMailHandoffStatus.INACTIVE,
  hasHandoffSucceeded: false,
  isQueueReturnNoticeVisible: false,
  astSignMailMode: undefined,
  textOnSignMail: '',
  transferAstRelayResponse: null,
};

export const astSignMailSlice = createSlice({
  name: 'astSignMail',
  initialState: initialAstSignMailState,
  reducers: {
    claimAstSignMailHandoff: (state) => {
      state.handoffStatus = AstSignMailHandoffStatus.TRANSFERRING;
    },
    startAstSignMailHandoff: (state) => {
      state.handoffStatus = AstSignMailHandoffStatus.SHOWING;
    },
    showAstSignMailQueueReturnNotice: (state) => {
      state.isQueueReturnNoticeVisible = true;
    },
    dismissAstSignMailQueueReturnNotice: (state) => {
      state.isQueueReturnNoticeVisible = false;
    },
    endAstSignMailHandoff: (state) => {
      state.handoffStatus = AstSignMailHandoffStatus.INACTIVE;
      state.transferAstRelayResponse = null;
    },
    resetAstSignMail: () => initialAstSignMailState,
    astSignMailModeReceived: (
      state,
      action: PayloadAction<number | undefined>
    ) => {
      state.astSignMailMode = action.payload;
    },
    textOnSignMailChanged: (state, action: PayloadAction<string>) => {
      state.textOnSignMail = action.payload;
    },
    transferAstRelayResponseReceived: (
      state,
      action: PayloadAction<TransferAstRelayResponse>
    ) => {
      state.transferAstRelayResponse = action.payload;
      state.hasHandoffSucceeded = true;
    },
  },
  extraReducers: (builder) => {
    // Avoid a call-feature import cycle while clearing an in-flight handoff
    // at the same reset boundary as the rest of the call state.
    builder.addMatcher(
      (action) => action.type === RESET_CALL_ACTION_TYPE,
      (state) => ({
        ...initialAstSignMailState,
        isQueueReturnNoticeVisible: state.isQueueReturnNoticeVisible,
      })
    );
  },
});

export const {
  claimAstSignMailHandoff,
  startAstSignMailHandoff,
  showAstSignMailQueueReturnNotice,
  dismissAstSignMailQueueReturnNotice,
  endAstSignMailHandoff,
  resetAstSignMail,
  astSignMailModeReceived,
  textOnSignMailChanged,
  transferAstRelayResponseReceived,
} = astSignMailSlice.actions;

export const astSignMailReducer = astSignMailSlice.reducer;
